import { Link } from 'react-router';
import { ScoreMark } from '@/components/ui/ScoreMark';
import type { HoleScore } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { EM_DASH, fmtToPar } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import styles from './Scorecard.module.css';
import { CheckIcon, CloseIcon } from '@/components/icons';

export interface ScorecardHole {
  /** Logical hole number, 1..holesPlayed. */
  number: number;
  par: number;
  /** Stroke index. Shown on desktop only — mobile has no room and it is reference data. */
  strokeIndex?: number;
  score?: HoleScore;
}

interface ScorecardProps {
  holes: ScorecardHole[];
  /** Makes each hole column a link into the viewer. */
  linkTo?: (holeNumber: number) => string;
  showStrokeIndex?: boolean;
  /**
   * `round` — the played card: strokes, putts, fairway and GIR.
   * `course` — the course's own card, which has no score. Rendering the score rows there
   *   fills four lines with "—" and implies data is missing rather than inapplicable.
   */
  variant?: 'round' | 'course';
  /** Yardage row for the course card, keyed by hole number. */
  distances?: Map<number, string>;
  /** Column header for the distance row, e.g. the tee name. */
  distanceLabel?: string;
}

/** OUT / IN / TOT segments. A 9-hole round has no split, so it gets TOT only. */
function segmentsFor(holes: ScorecardHole[]) {
  if (holes.length <= 9) return [{ key: 'TOT', from: 1, to: holes.length }];
  const half = Math.floor(holes.length / 2);
  return [
    { key: 'OUT', from: 1, to: half },
    { key: 'IN', from: half + 1, to: holes.length },
    { key: 'TOT', from: 1, to: holes.length },
  ];
}

function sum(
  holes: ScorecardHole[],
  from: number,
  to: number,
  pick: (h: ScorecardHole) => number | null | undefined,
) {
  let total = 0;
  let any = false;
  for (const h of holes) {
    if (h.number < from || h.number > to) continue;
    const v = pick(h);
    if (v == null) continue;
    total += v;
    any = true;
  }
  return any ? total : null;
}

export function Scorecard({
  holes,
  linkTo,
  showStrokeIndex = false,
  variant = 'round',
  distances,
  distanceLabel,
}: ScorecardProps) {
  const segments = segmentsFor(holes);
  const isCourse = variant === 'course';

  return (
    <div className={cx(styles.scroller, 'scroll-x')}>
      <div className={styles.grid}>
        <div className={styles.labels}>
          <div className={styles.headLabel}>{t('scorecard.hole')}</div>
          <div className={cx(styles.cell, styles.labelCell, styles.rowPar)}>{t('scorecard.par')}</div>
          {showStrokeIndex && (
            <div className={cx(styles.cell, styles.labelCell, styles.rowSi)}>{t('scorecard.si')}</div>
          )}
          {isCourse ? (
            <div
              className={cx(
                styles.cell,
                styles.labelCell,
                styles.rowStrokes,
                styles.strokesLabel,
                styles.last,
              )}
            >
              {distanceLabel ?? t('scorecard.distance')}
            </div>
          ) : (
            <>
              <div
                className={cx(
                  styles.cell,
                  styles.labelCell,
                  styles.rowStrokes,
                  styles.strokesLabel,
                )}
              >
                {t('scorecard.strokes')}
              </div>
              <div className={cx(styles.cell, styles.labelCell, styles.rowPutts)}>
                {t('scorecard.putts')}
              </div>
              <div className={cx(styles.cell, styles.labelCell, styles.rowFw)}>
                {t('scorecard.fw')}
              </div>
              <div className={cx(styles.cell, styles.labelCell, styles.rowGir, styles.last)}>
                {t('scorecard.gir')}
              </div>
            </>
          )}
        </div>

        {holes.map((h) => {
          const cells = (
            <>
              <div className={cx(styles.cell, styles.head)}>{h.number}</div>
              <div className={cx(styles.cell, styles.rowPar)}>{h.par}</div>
              {showStrokeIndex && (
                <div className={cx(styles.cell, styles.rowSi)}>{h.strokeIndex ?? EM_DASH}</div>
              )}
              {isCourse ? (
                <div className={cx(styles.cell, styles.rowStrokes, styles.last, styles.totalValue)}>
                  {distances?.get(h.number) ?? EM_DASH}
                </div>
              ) : (
                <>
                  <div className={cx(styles.cell, styles.rowStrokes)}>
                    <ScoreMark strokes={h.score?.strokes ?? null} par={h.par} size="sm" />
                  </div>
                  <div className={cx(styles.cell, styles.rowPutts)}>
                    {h.score?.putts ?? EM_DASH}
                  </div>
                  <div className={cx(styles.cell, styles.rowFw)}>
                    {/* A par 3 has no fairway to hit — the API forces null, and so does the card. */}
                    <span className={h.score?.fairwayHit ? styles.hit : styles.miss}>
                      {h.par === 3 || h.score?.fairwayHit == null ? (
                        EM_DASH
                      ) : h.score.fairwayHit ? (
                        <CheckIcon size={13} />
                      ) : (
                        <CloseIcon size={13} />
                      )}
                    </span>
                  </div>
                  <div className={cx(styles.cell, styles.rowGir, styles.last)}>
                    <span className={h.score?.greenInRegulation ? styles.hit : styles.miss}>
                      {h.score?.greenInRegulation == null ? (
                        EM_DASH
                      ) : h.score.greenInRegulation ? (
                        <CheckIcon size={13} />
                      ) : (
                        <CloseIcon size={13} />
                      )}
                    </span>
                  </div>
                </>
              )}
            </>
          );

          return linkTo ? (
            <Link key={h.number} to={linkTo(h.number)} className={cx(styles.col, styles.holeLink)}>
              {cells}
            </Link>
          ) : (
            <div key={h.number} className={styles.col}>
              {cells}
            </div>
          );
        })}

        {segments.map((seg) => {
          const par = sum(holes, seg.from, seg.to, (h) => h.par);
          const strokes = sum(holes, seg.from, seg.to, (h) => h.score?.strokes);
          const putts = sum(holes, seg.from, seg.to, (h) => h.score?.putts);
          return (
            <div key={seg.key} className={cx(styles.col, styles.colTotal)}>
              <div className={cx(styles.cell, styles.head)}>{seg.key}</div>
              <div className={cx(styles.cell, styles.rowPar, styles.totalValue)}>
                {par ?? EM_DASH}
              </div>
              {showStrokeIndex && <div className={cx(styles.cell, styles.rowSi)}>{EM_DASH}</div>}
              {isCourse ? (
                <div className={cx(styles.cell, styles.rowStrokes, styles.last)}>{EM_DASH}</div>
              ) : (
                <>
                  <div className={cx(styles.cell, styles.rowStrokes, styles.totalValue)}>
                    {strokes ?? EM_DASH}
                  </div>
                  <div className={cx(styles.cell, styles.rowPutts, styles.totalValue)}>
                    {putts ?? EM_DASH}
                  </div>
                  <div className={cx(styles.cell, styles.rowFw)}>{EM_DASH}</div>
                  <div className={cx(styles.cell, styles.rowGir, styles.last)}>{EM_DASH}</div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The shape legend. Needed because par is notated by the *absence* of a mark. */
export function ScorecardLegend() {
  return (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <span className={styles.legendRing} aria-hidden="true" />
        {t('scorecard.legend.birdie')}
      </span>
      <span className={styles.legendItem}>
        <span className={styles.legendSquare} aria-hidden="true" />
        {t('scorecard.legend.bogey')}
      </span>
      <span className={styles.legendItem}>
        <span className={styles.legendDouble} aria-hidden="true" />
        {t('scorecard.legend.double')}
      </span>
      <span>{t('scorecard.legend.par')}</span>
    </div>
  );
}

/** Score relative to par for a segment, e.g. "+12". Exported for the round header. */
export { fmtToPar };
